import {
  ApiGetHook,
  ApiGetHookOptions,
  ApiMutateHook,
  useApiGet,
  useApiMutate,
} from "@skanska/vsa-client-auth";
import { CacheKeys } from "../constants/CacheKeys";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ApiMethods } from "../models/ApiMethods";
import { ViewDefinition } from "../models/Field";
import { QueryFilter } from "../models/QueryFilter";
import {
  ActivateToolRequest,
  AdminDeleteToolRequest,
  CreateCloneToolRequest,
  DocumentHealthCheckJobHistory,
  MassCreateToolDraftRequest,
  MassCreateToolDraftResponse,
  MassPublishToolRequest,
  MassPublishToolResponse,
  PublishToolRequest,
  RelinkToolFileRequest,
  Tool,
  UpdateToolsRequest,
} from "../models/Tool";
import { ApiQuery, useApiQuery } from "./useApiQuery";

export function useGetAllCurrentPublishedTools(
  defaultSortBy: string,
  defaultSortAsc: boolean,
  defaultFilters?: QueryFilter[],
  pageSize: number = 50
): ApiQuery<Tool> {
  return useApiQuery<Tool>(
    ApiEndpoints.ToolsCurrent,
    defaultSortBy,
    defaultSortAsc,
    CacheKeys.Tool.ToolList,
    defaultFilters,
    pageSize
  );
}

export function useGetPublishedTool(
  versionId: string,
  oldVersion: boolean
): ApiGetHook<Tool> {
  return useApiGet<Tool>(
    oldVersion ? ApiEndpoints.ToolOldVersion : ApiEndpoints.Tools,
    versionId,
    `tool-${versionId}`
  );
}

export function useGetCurrentPublishedTool(toolId: string): ApiGetHook<Tool> {
  return useApiGet<Tool>(
    ApiEndpoints.ToolsCurrent,
    toolId,
    `current-tool-${toolId}`
  );
}

export function useDoesToolHaveDraft(
  toolId: string,
  options?: ApiGetHookOptions
): ApiGetHook<string | null> {
  return useApiGet<string | null>(
    `${ApiEndpoints.ToolsCurrent}/${toolId}/hasdraft`,
    null,
    `tool-${toolId}-hasdraft`,
    options
  );
}

export function useGetCurrentToolDraft(toolId?: string): ApiGetHook<Tool> {
  const draftVersion = useDoesToolHaveDraft(toolId ?? "", {
    enabled: !!toolId,
  });

  const draft = useApiGet<Tool>(
    ApiEndpoints.ToolDrafts,
    draftVersion.data,
    `tool-${draftVersion.data}`,
    {
      enabled: !!draftVersion.data,
    }
  );

  return {
    data: draft.data,
    isLoading: draftVersion.isLoading || draft.isLoading,
    error: draftVersion.error ?? draft.error,
    isRefetching: draftVersion.isRefetching || draft.isRefetching,
  };
}

export function useGetToolDraft(versionId: string): ApiGetHook<Tool> {
  return useApiGet<Tool>(
    ApiEndpoints.ToolDrafts,
    versionId,
    `tool-${versionId}`
  );
}

export function useGetToolHistory(toolId: string): ApiGetHook<Tool[]> {
  return useApiGet<Tool[]>(
    ApiEndpoints.ToolFullHistory,
    toolId,
    `tool-history-${toolId}`
  );
}

export function useCreateToolDraft(
  toolId?: string
): ApiMutateHook<Tool, FormData> {
  return useApiMutate<Tool, FormData>(
    ApiEndpoints.ToolDrafts,
    ApiMethods.Post,
    null,
    null,
    [
      `tool-${toolId}-hasdraft`,
      CacheKeys.Tool.ToolDraftList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Link.LinkArchivedList,
    ],
    null,
    true
  );
}

export function useUpdateToolDraft(
  versionId: string
): ApiMutateHook<Tool, FormData> {
  return useApiMutate<Tool, FormData>(
    ApiEndpoints.ToolDrafts,
    ApiMethods.Patch,
    null,
    null,
    [
      `tool-${versionId}`,
      CacheKeys.Tool.ToolDraftList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Link.LinkArchivedList,
    ],
    null,
    true
  );
}

export function useUpdateToolDrafts(): ApiMutateHook<Tool, UpdateToolsRequest> {
  return useApiMutate<Tool, UpdateToolsRequest>(
    ApiEndpoints.ToolDraftsQuickEdit,
    ApiMethods.Patch,
    null,
    null,
    [
      CacheKeys.Tool.ToolDraftList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Link.LinkArchivedList,
    ]
  );
}

export function useDeleteToolDraft(
  versionId: string,
  toolId: string
): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    ApiEndpoints.ToolDrafts,
    ApiMethods.Delete,
    versionId,
    null,
    [
      `tool-${toolId}-hasdraft`,
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Tool.ToolDraftList,
    ],
    [`tool-${versionId}`]
  );
}

export function useCancelToolDraft(
  versionId: string,
  toolId: string
): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    `${ApiEndpoints.ToolDrafts}/${versionId}/cancel`,
    ApiMethods.Delete,
    undefined,
    null,
    [
      `tool-${toolId}-hasdraft`,
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Tool.ToolDraftList,
    ],
    [`tool-${versionId}`]
  );
}

export function useValidateDraftFile(): ApiMutateHook<
  boolean,
  { versionId: string }
> {
  return useApiMutate<boolean, { versionId: string }>(
    ApiEndpoints.ToolDraftValidateFile,
    ApiMethods.Post
  );
}

export function usePublishToolDraft(
  versionId: string,
  toolId?: string
): ApiMutateHook<Tool, PublishToolRequest> {
  return useApiMutate<Tool, PublishToolRequest>(
    ApiEndpoints.ToolPublished,
    ApiMethods.Post,
    null,
    null,
    [
      `tool-${toolId}-hasdraft`,
      `current-tool-${toolId}`,
      `tool-history-${toolId}`,
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
      CacheKeys.Tool.ToolDraftList,
    ],
    [`tool-${versionId}`]
  );
}

export function useSaveAndPublishToolDraft(
  versionId: string,
  toolId?: string
): ApiMutateHook<Tool, FormData> {
  return useApiMutate<Tool, FormData>(
    ApiEndpoints.ToolDraftsSaveAndPublish,
    ApiMethods.Post,
    null,
    null,
    [
      `tool-${toolId}-hasdraft`,
      `current-tool-${toolId}`,
      `tool-history-${toolId}`,
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
      CacheKeys.Tool.ToolDraftList,
    ],
    [`tool-${versionId}`],
    true
  );
}

export function useMassPublishToolDrafts(): ApiMutateHook<
  MassPublishToolResponse,
  MassPublishToolRequest
> {
  return useApiMutate<MassPublishToolResponse, MassPublishToolRequest>(
    `${ApiEndpoints.Tools}/masspublish`,
    ApiMethods.Post,
    null,
    null,
    [],
    [
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
      CacheKeys.Tool.ToolDraftList,
    ]
  );
}

export function useMassCreateToolDrafts(): ApiMutateHook<
  MassCreateToolDraftResponse,
  MassCreateToolDraftRequest
> {
  return useApiMutate<MassCreateToolDraftResponse, MassCreateToolDraftRequest>(
    ApiEndpoints.ToolMassCreateDraft,
    ApiMethods.Post,
    null,
    null,
    [],
    [
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
      CacheKeys.Tool.ToolDraftList,
    ]
  );
}

export function useGetLinkFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    ApiEndpoints.ToolLinkFields,
    null,
    `link-fields`
  );
}

export function useGetLinkQuickEditFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    ApiEndpoints.ToolLinkQuickEditFields,
    null,
    "link-quick-edit-fields"
  );
}

export function useGetDocumentFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    ApiEndpoints.ToolDocumentFields,
    null,
    `document-fields`
  );
}

export function useGetDocumentQuickEditFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    ApiEndpoints.ToolDocumentQuickEditFields,
    null,
    "document-quick-edit-fields"
  );
}

export function useCloneToolDraft(
  toolId: string
): ApiMutateHook<Tool, CreateCloneToolRequest> {
  const keys = [
    ApiEndpoints.ToolsCurrent,
    `current-tool-${toolId}`,
    `tool-${toolId}-hasdraft`,
    CacheKeys.Tool.ToolList,
    CacheKeys.Document.DocumentList,
    CacheKeys.Document.DocumentArchivedList,
    CacheKeys.Document.DocumentDraftList,
    CacheKeys.Link.LinkList,
    CacheKeys.Link.LinkArchivedList,
    CacheKeys.Link.LinkDraftList,
    CacheKeys.Tool.ToolDraftList,
  ];
  return useApiMutate<Tool, CreateCloneToolRequest>(
    ApiEndpoints.ToolDraftClone,
    ApiMethods.Post,
    null,
    null,
    keys,
    null
  );
}

export function useActivateTool(
  versionId: string,
  toolId: string
): ApiMutateHook<Tool, ActivateToolRequest> {
  return useApiMutate<Tool, ActivateToolRequest>(
    ApiEndpoints.ToolActivate,
    ApiMethods.Post,
    null,
    null,
    [
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      `tool-${versionId}`,
      `current-tool-${toolId}`,
      `tool-history-${toolId}`,
      `tool-${toolId}-hasdraft`,
      CacheKeys.Activity.SingleActivity,
    ]
  );
}

export function useGetCurrentPublishedDocumentsNames(): ApiGetHook<string[]> {
  return useApiGet<string[]>(ApiEndpoints.ToolDocumentNames);
}

export function useAdminDeleteTool(
  toolId: string
): ApiMutateHook<void, AdminDeleteToolRequest> {
  return useApiMutate<void, AdminDeleteToolRequest>(
    `${ApiEndpoints.ToolAdminDelete}/${toolId}`,
    ApiMethods.Delete,
    null,
    null,
    [
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
      CacheKeys.Document.DocumentDraftList,
      CacheKeys.Link.LinkList,
      CacheKeys.Link.LinkArchivedList,
      CacheKeys.Link.LinkDraftList,
      CacheKeys.Tool.ToolDraftList,
    ]
  );
}

export function useHealthCheckStatus(
  tick?: number
): ApiGetHook<DocumentHealthCheckJobHistory> {
  return useApiGet<DocumentHealthCheckJobHistory>(
    ApiEndpoints.ToolAdminHealthCheckStatus,
    null,
    tick !== undefined ? `health-check-status-${tick}` : "health-check-status"
  );
}

export function useStartHealthCheck(): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    ApiEndpoints.ToolAdminHealthCheckStart,
    ApiMethods.Post,
    null,
    null,
    ["health-check-status", "health-check-history"]
  );
}

export function useHealthCheckHistory(): ApiGetHook<
  DocumentHealthCheckJobHistory[]
> {
  return useApiGet<DocumentHealthCheckJobHistory[]>(
    `${ApiEndpoints.ReindexJobHistory}?jobType=DocumentHealthCheck`,
    null,
    "health-check-history"
  );
}

export function useRelinkToolFile(
  toolId: string
): ApiMutateHook<void, RelinkToolFileRequest> {
  return useApiMutate<void, RelinkToolFileRequest>(
    `${ApiEndpoints.ToolAdminRelink}/${toolId}/relink`,
    ApiMethods.Post,
    null,
    null,
    [
      CacheKeys.Tool.ToolList,
      CacheKeys.Document.DocumentList,
      CacheKeys.Document.DocumentArchivedList,
    ]
  );
}
